/**
 * lib/atRisk.ts
 * ------------------------------------------------------------------
 * "At-risk student" classification — replicates Al-Nur's EXACT
 * rule (not an invented threshold), because this drives real
 * academic interventions for real students and must be defensible.
 *
 * Al-Nur rule (verified against the original ResultsAnalysis):
 *   • A subject is FAILED if its mark < passMark.
 *   • A subject is BORDERLINE if passMark <= mark < passMark+margin.
 *   • English is a gateway subject: failing English (when not ABS)
 *     is critical on its own. "English" excludes "English
 *     Literature".
 *   • overallResult = FAIL if (failedCount >= 2) OR englishFailed,
 *     else PASS.
 *   • isAtRisk if: any failed subject, OR >=2 borderline subjects,
 *     OR average < (passMark + 5).
 *   • Tiers: CRITICAL = at-risk AND overallResult FAIL;
 *            BORDERLINE = at-risk AND overallResult PASS.
 *
 * Marks come from the SAME deriveMark the report card uses (the
 * caller passes the derived per-subject numeric mark), so this is
 * consistent with what families actually see. ABS is excluded
 * from numeric reasoning exactly as Al-Nur did.
 * ------------------------------------------------------------------
 */
import type { Student, Subject } from '@/types';

export type RiskTier = 'critical' | 'borderline';

export interface SubjectMark {
  subjectId: string;
  subjectName: string;
  mark: number;
}

export interface AtRiskStudent {
  studentId: string;
  fullName: string;
  average: number | null;
  failedSubjects: SubjectMark[];
  borderlineSubjects: SubjectMark[];
  englishFailed: boolean;
  overallResult: 'PASS' | 'FAIL';
  tier: RiskTier;
}

export interface AtRiskInput {
  students: Student[]; // class roster
  subjects: Subject[]; // subjects in play (for names)
  passMark: number;
  // matrix[studentId][subjectId] = derived numeric mark, or null
  // (null = no usable mark / ABS) — exactly the shape
  // ProgressDashboard already builds via deriveMark.
  matrix: Record<string, Record<string, number | null>>;
  // how far above the pass mark still counts as "borderline"
  borderlineMargin?: number; // default 10 (Al-Nur used a band)
}

function isEnglish(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('english') && !n.includes('literature');
}

export function classifyAtRisk(
  input: AtRiskInput,
): AtRiskStudent[] {
  const {
    students,
    subjects,
    passMark,
    matrix,
    borderlineMargin = 10,
  } = input;
  const sName = (id: string) =>
    subjects.find((s) => s.id === id)?.name ?? id;
  const borderlineMax = passMark + borderlineMargin;
  // Al-Nur: average threshold scales with the pass mark.
  const avgThreshold = passMark + 5;

  const out: AtRiskStudent[] = [];

  for (const stu of students) {
    const row = matrix[stu.id] ?? {};
    const failedSubjects: SubjectMark[] = [];
    const borderlineSubjects: SubjectMark[] = [];
    const numeric: number[] = [];
    let englishFailed = false;

    for (const sid of stu.subjects ?? []) {
      const m = row[sid];
      if (m === null || m === undefined) continue; // ABS / none
      numeric.push(m);
      const nm = sName(sid);
      if (m < passMark) {
        failedSubjects.push({
          subjectId: sid,
          subjectName: nm,
          mark: m,
        });
        if (isEnglish(nm)) englishFailed = true;
      } else if (m < borderlineMax) {
        borderlineSubjects.push({
          subjectId: sid,
          subjectName: nm,
          mark: m,
        });
      }
    }

    const average =
      numeric.length > 0
        ? Math.round(
            numeric.reduce((a, b) => a + b, 0) / numeric.length,
          )
        : null;

    const overallResult: 'PASS' | 'FAIL' =
      failedSubjects.length >= 2 || englishFailed
        ? 'FAIL'
        : 'PASS';

    const isAtRisk =
      failedSubjects.length > 0 ||
      borderlineSubjects.length >= 2 ||
      (average !== null && average < avgThreshold);

    if (!isAtRisk) continue;

    out.push({
      studentId: stu.id,
      fullName: stu.fullName,
      average,
      failedSubjects,
      borderlineSubjects,
      englishFailed,
      overallResult,
      tier: overallResult === 'FAIL' ? 'critical' : 'borderline',
    });
  }

  // Al-Nur ordering: most failed first, then lowest average.
  out.sort(
    (a, b) =>
      b.failedSubjects.length - a.failedSubjects.length ||
      (a.average ?? 100) - (b.average ?? 100),
  );
  return out;
}
